import React, { useState } from "react";

export default function SysmeshLanding() {
  const [hoveredCard, setHoveredCard] = useState(null);
  const [activeCase, setActiveCase] = useState(0);

  const styles = {
    container: {
      minHeight: "100vh",
      background: "#ffffff",
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", "Red Hat Display", Roboto, "Helvetica Neue", Arial, sans-serif',
      position: "relative",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
    },
    header: {
      padding: "24px 80px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      background: "#ffffff",
      borderBottom: "1px solid #e5e5e5",
      position: "sticky",
      top: 0,
      zIndex: 100,
      boxShadow: "0 2px 4px rgba(0,0,0,0.04)",
    },
    logo: {
      fontSize: "28px",
      fontWeight: "700",
      color: "#ee0000",
      letterSpacing: "-0.5px",
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    logoIcon: {
      width: "36px",
      height: "36px",
      background: "linear-gradient(135deg, #ee0000 0%, #cc0000 100%)",
      borderRadius: "8px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#ffffff",
      fontSize: "20px",
      fontWeight: "bold",
    },
    buttonGroup: {
      display: "flex",
      gap: "16px",
      alignItems: "center",
    },
    button: (variant) => ({
      padding: "12px 32px",
      border: "none",
      background: variant === "primary" ? "#ee0000" : "transparent",
      color: variant === "primary" ? "#ffffff" : "#151515",
      cursor: "pointer",
      fontSize: "15px",
      fontWeight: "600",
      letterSpacing: "-0.2px",
      transition: "all 0.2s ease",
      borderRadius: "6px",
      boxShadow:
        variant === "primary" ? "0 2px 8px rgba(238, 0, 0, 0.2)" : "none",
    }),
    hero: {
      padding: "100px 80px 120px",
      background: "linear-gradient(180deg, #ffffff 0%, #f8f8f8 100%)",
      position: "relative",
    },
    heroContainer: {
      maxWidth: "1400px",
      margin: "0 auto",
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "80px",
      alignItems: "center",
    },
    heroContent: {
      maxWidth: "600px",
    },
    heroTitle: {
      fontSize: "64px",
      fontWeight: "700",
      color: "#151515",
      lineHeight: "1.1",
      marginBottom: "24px",
      letterSpacing: "-2px",
    },
    heroTitleAccent: {
      color: "#ee0000",
      display: "block",
    },
    heroSubtitle: {
      fontSize: "22px",
      color: "#525252",
      lineHeight: "1.5",
      marginBottom: "40px",
      fontWeight: "400",
    },
    heroCTA: {
      display: "flex",
      gap: "16px",
      alignItems: "center",
      marginBottom: "50px",
    },
    primaryCTA: {
      padding: "18px 44px",
      background: "#ee0000",
      color: "#ffffff",
      border: "none",
      borderRadius: "8px",
      fontSize: "17px",
      fontWeight: "600",
      cursor: "pointer",
      transition: "all 0.2s ease",
      boxShadow: "0 4px 12px rgba(238, 0, 0, 0.25)",
      letterSpacing: "-0.2px",
    },
    secondaryCTA: {
      padding: "18px 44px",
      background: "#ffffff",
      color: "#151515",
      border: "2px solid #e5e5e5",
      borderRadius: "8px",
      fontSize: "17px",
      fontWeight: "600",
      cursor: "pointer",
      transition: "all 0.2s ease",
      letterSpacing: "-0.2px",
    },
    heroStats: {
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: "30px",
      paddingTop: "40px",
      borderTop: "1px solid #e5e5e5",
    },
    stat: {
      textAlign: "left",
    },
    statNumber: {
      fontSize: "42px",
      fontWeight: "700",
      color: "#ee0000",
      marginBottom: "6px",
      letterSpacing: "-1px",
    },
    statLabel: {
      fontSize: "14px",
      color: "#525252",
      fontWeight: "500",
      lineHeight: "1.4",
    },
    heroVisual: {
      background: "#ffffff",
      borderRadius: "16px",
      padding: "40px",
      boxShadow: "0 20px 60px rgba(0,0,0,0.08)",
      border: "1px solid #e5e5e5",
    },
    dashboardPreview: {
      width: "100%",
      height: "400px",
      background: "linear-gradient(135deg, #f8f8f8 0%, #ffffff 100%)",
      borderRadius: "8px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "48px",
      color: "#ee0000",
      border: "2px solid #f0f0f0",
    },
    successCasesSection: {
      padding: "100px 80px",
      background: "#ffffff",
    },
    casesContainer: {
      maxWidth: "1400px",
      margin: "0 auto",
    },
    sectionTitle: {
      fontSize: "42px",
      fontWeight: "700",
      color: "#151515",
      textAlign: "center",
      marginBottom: "16px",
      letterSpacing: "-1px",
    },
    sectionSubtitle: {
      fontSize: "20px",
      color: "#525252",
      textAlign: "center",
      marginBottom: "60px",
      maxWidth: "700px",
      margin: "0 auto 60px",
    },
    casesGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: "32px",
      marginBottom: "60px",
    },
    caseCard: (isActive) => ({
      padding: "40px",
      background: isActive ? "#ee0000" : "#ffffff",
      borderRadius: "12px",
      border: isActive ? "none" : "1px solid #e5e5e5",
      cursor: "pointer",
      transition: "all 0.3s ease",
      boxShadow: isActive
        ? "0 12px 32px rgba(238, 0, 0, 0.25)"
        : "0 2px 8px rgba(0,0,0,0.04)",
    }),
    caseCompany: (isActive) => ({
      fontSize: "20px",
      fontWeight: "700",
      color: isActive ? "#ffffff" : "#151515",
      marginBottom: "12px",
      letterSpacing: "-0.5px",
    }),
    caseIndustry: (isActive) => ({
      fontSize: "14px",
      color: isActive ? "rgba(255,255,255,0.8)" : "#888",
      marginBottom: "20px",
      fontWeight: "500",
      textTransform: "uppercase",
      letterSpacing: "1px",
    }),
    caseMetric: (isActive) => ({
      fontSize: "48px",
      fontWeight: "700",
      color: isActive ? "#ffffff" : "#ee0000",
      marginBottom: "8px",
      letterSpacing: "-1px",
    }),
    caseMetricLabel: (isActive) => ({
      fontSize: "16px",
      color: isActive ? "rgba(255,255,255,0.9)" : "#525252",
      fontWeight: "500",
    }),
    caseDetail: {
      background: "#f8f8f8",
      padding: "60px",
      borderRadius: "16px",
      marginTop: "40px",
    },
    caseDetailTitle: {
      fontSize: "28px",
      fontWeight: "700",
      color: "#151515",
      marginBottom: "20px",
      letterSpacing: "-0.5px",
    },
    caseDetailText: {
      fontSize: "18px",
      color: "#525252",
      lineHeight: "1.7",
      marginBottom: "30px",
    },
    caseDetailQuote: {
      fontSize: "20px",
      color: "#151515",
      fontStyle: "italic",
      padding: "30px",
      background: "#ffffff",
      borderLeft: "4px solid #ee0000",
      borderRadius: "8px",
      marginTop: "30px",
    },
    featuresSection: {
      padding: "100px 80px",
      background: "#f8f8f8",
    },
    featuresContainer: {
      maxWidth: "1400px",
      margin: "0 auto",
    },
    featuresGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
      gap: "32px",
    },
    featureCard: (isHovered) => ({
      padding: "40px",
      background: "#ffffff",
      borderRadius: "12px",
      transition: "all 0.3s ease",
      border: "1px solid #e5e5e5",
      cursor: "pointer",
      transform: isHovered ? "translateY(-8px)" : "translateY(0)",
      boxShadow: isHovered
        ? "0 12px 32px rgba(0,0,0,0.12)"
        : "0 2px 8px rgba(0,0,0,0.04)",
    }),
    featureIcon: {
      width: "56px",
      height: "56px",
      background: "linear-gradient(135deg, #fee 0%, #fdd 100%)",
      borderRadius: "12px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "28px",
      marginBottom: "24px",
    },
    featureTitle: {
      fontSize: "22px",
      fontWeight: "700",
      color: "#151515",
      marginBottom: "12px",
      letterSpacing: "-0.5px",
    },
    featureText: {
      fontSize: "16px",
      color: "#525252",
      lineHeight: "1.6",
      fontWeight: "400",
    },
    ctaSection: {
      padding: "100px 80px",
      background: "linear-gradient(135deg, #ee0000 0%, #cc0000 100%)",
      textAlign: "center",
    },
    ctaTitle: {
      fontSize: "48px",
      fontWeight: "700",
      color: "#ffffff",
      marginBottom: "24px",
      letterSpacing: "-1px",
    },
    ctaSubtitle: {
      fontSize: "20px",
      color: "rgba(255,255,255,0.9)",
      marginBottom: "40px",
      maxWidth: "700px",
      margin: "0 auto 40px",
    },
    ctaButton: {
      padding: "18px 50px",
      background: "#ffffff",
      color: "#ee0000",
      border: "none",
      borderRadius: "8px",
      fontSize: "18px",
      fontWeight: "700",
      cursor: "pointer",
      transition: "all 0.2s ease",
      boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
      letterSpacing: "-0.2px",
    },
  };

  const successCases = [
    {
      company: "TechCorp Global",
      industry: "Tecnología",
      metric: "70%",
      metricLabel: "Reducción tiempo deploy",
      description:
        "TechCorp Global, una empresa de software con 500+ empleados, logró transformar completamente su pipeline de desarrollo. Antes de Sysmesh, los deploys tomaban horas y requerían intervención manual constante.",
      quote:
        "Sysmesh nos permitió escalar de 50 a 200 microservicios sin aumentar el equipo de DevOps. El ROI fue evidente en el primer trimestre.",
      author: "María González, CTO",
    },
    {
      company: "RetailMax",
      industry: "E-commerce",
      metric: "99.99%",
      metricLabel: "Uptime garantizado",
      description:
        "RetailMax procesaba 10M de transacciones mensuales con infraestructura fragmentada. Sysmesh centralizó el monitoreo y automatizó la respuesta a incidentes, eliminando el 95% de las interrupciones.",
      quote:
        "Durante Black Friday procesamos 3x más tráfico sin caídas. Sysmesh se pagó solo con las ventas que hubieran sido imposibles antes.",
      author: "Carlos Ruiz, VP Engineering",
    },
    {
      company: "FinanceHub",
      industry: "Fintech",
      metric: "$2M",
      metricLabel: "Ahorro anual operativo",
      description:
        "FinanceHub necesitaba cumplir con regulaciones estrictas mientras escalaba. Sysmesh proporcionó auditorías automáticas, control de accesos granular y reducción de costos de AWS del 45%.",
      quote:
        "La visibilidad y trazabilidad que nos da Sysmesh es crítica para nuestras auditorías. Pasamos compliance en el primer intento.",
      author: "Ana Martínez, COO",
    },
  ];

  const features = [
    {
      icon: "📊",
      title: "Visibilidad Completa",
      text: "Dashboard unificado con métricas en tiempo real de toda tu infraestructura. Toma decisiones informadas con datos precisos.",
    },
    {
      icon: "⚡",
      title: "Automatización Inteligente",
      text: "Reduce costos operativos hasta un 60% con pipelines de CI/CD y gestión automatizada de recursos.",
    },
    {
      icon: "🔒",
      title: "Seguridad Enterprise",
      text: "Cumplimiento normativo, control de accesos basado en roles y auditorías completas para proteger tus activos críticos.",
    },
    {
      icon: "📈",
      title: "Escalabilidad Sin Límites",
      text: "Desde 10 hasta 10,000+ servidores. Arquitectura cloud-native que crece con tu negocio sin fricción.",
    },
    {
      icon: "🔧",
      title: "Integración Universal",
      text: "Compatible con AWS, Azure, GCP, Kubernetes, Docker y todas tus herramientas DevOps existentes.",
    },
    {
      icon: "💼",
      title: "Soporte Dedicado",
      text: "Equipo técnico disponible 24/7 y SLA garantizado del 99.9% para mantener tus operaciones sin interrupciones.",
    },
  ];

  const handleAuth = () => {
    window.location.href = '/auth';
  };

  return (
    <div style={styles.container}>
      <style>{`
        button:hover {
          transform: translateY(-2px);
          opacity: 0.95;
        }
        @media (max-width: 1024px) {
          .hero-container { grid-template-columns: 1fr !important; }
          .cases-grid { grid-template-columns: 1fr !important; }
          .features-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      <header style={styles.header}>
        <div style={styles.logo}>
          <div style={styles.logoIcon}>SM</div>
          <span>Sysmesh</span>
        </div>
        <div style={styles.buttonGroup}>
          <button style={styles.button("secondary")} onClick={handleAuth}>
            Iniciar Sesión
          </button>
          <button style={styles.button("primary")}>Solicitar Demo</button>
        </div>
      </header>

      {/* Hero Section */}
      <section style={styles.hero}>
        <div style={styles.heroContainer} className="hero-container">
          <div style={styles.heroContent}>
            <h1 style={styles.heroTitle}>
              La plataforma de gestión{" "}
              <span style={styles.heroTitleAccent}>
                que escala con tu negocio
              </span>
            </h1>
            <p style={styles.heroSubtitle}>
              Centraliza, automatiza y optimiza toda tu infraestructura
              tecnológica desde una única plataforma. Diseñada para equipos que
              buscan eficiencia y resultados medibles.
            </p>
            <div style={styles.heroCTA}>
              <button style={styles.primaryCTA}>Comenzar Ahora</button>
              <button style={styles.secondaryCTA}>Ver Demo en Vivo</button>
            </div>
            <div style={styles.heroStats}>
              <div style={styles.stat}>
                <div style={styles.statNumber}>60%</div>
                <div style={styles.statLabel}>
                  Reducción de costos operativos
                </div>
              </div>
              <div style={styles.stat}>
                <div style={styles.statNumber}>99.9%</div>
                <div style={styles.statLabel}>Disponibilidad garantizada</div>
              </div>
              <div style={styles.stat}>
                <div style={styles.statNumber}>24/7</div>
                <div style={styles.statLabel}>Soporte técnico dedicado</div>
              </div>
            </div>
          </div>
          <div style={styles.heroVisual}>
            <div style={styles.dashboardPreview}>📊</div>
          </div>
        </div>
      </section>

      {/* Success Cases Section */}
      <section style={styles.successCasesSection}>
        <div style={styles.casesContainer}>
          <h2 style={styles.sectionTitle}>Casos de Éxito Reales</h2>
          <p style={styles.sectionSubtitle}>
            Empresas líderes que transformaron su infraestructura y
            multiplicaron sus resultados con Sysmesh
          </p>
          <div style={styles.casesGrid} className="cases-grid">
            {successCases.map((case_, idx) => (
              <div
                key={idx}
                style={styles.caseCard(activeCase === idx)}
                onClick={() => setActiveCase(idx)}
              >
                <div style={styles.caseCompany(activeCase === idx)}>
                  {case_.company}
                </div>
                <div style={styles.caseIndustry(activeCase === idx)}>
                  {case_.industry}
                </div>
                <div style={styles.caseMetric(activeCase === idx)}>
                  {case_.metric}
                </div>
                <div style={styles.caseMetricLabel(activeCase === idx)}>
                  {case_.metricLabel}
                </div>
              </div>
            ))}
          </div>
          <div style={styles.caseDetail}>
            <div style={styles.caseDetailTitle}>
              {successCases[activeCase].company}
            </div>
            <div style={styles.caseDetailText}>
              {successCases[activeCase].description}
            </div>
            <div style={styles.caseDetailQuote}>
              "{successCases[activeCase].quote}"
              <div
                style={{
                  marginTop: "15px",
                  fontSize: "16px",
                  fontStyle: "normal",
                  color: "#888",
                }}
              >
                — {successCases[activeCase].author}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section style={styles.featuresSection}>
        <div style={styles.featuresContainer}>
          <h2 style={styles.sectionTitle}>
            Todo lo que necesitas en un solo lugar
          </h2>
          <p style={styles.sectionSubtitle}>
            Herramientas empresariales que simplifican la complejidad y
            potencian la productividad de tu equipo
          </p>
          <div style={styles.featuresGrid} className="features-grid">
            {features.map((feature, idx) => (
              <div
                key={idx}
                style={styles.featureCard(hoveredCard === idx)}
                onMouseEnter={() => setHoveredCard(idx)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <div style={styles.featureIcon}>{feature.icon}</div>
                <h3 style={styles.featureTitle}>{feature.title}</h3>
                <p style={styles.featureText}>{feature.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={styles.ctaSection}>
        <h2 style={styles.ctaTitle}>
          Comienza a transformar tu infraestructura hoy
        </h2>
        <p style={styles.ctaSubtitle}>
          Únete a cientos de empresas que ya optimizaron sus operaciones con
          Sysmesh. Demo gratuita de 30 días sin tarjeta de crédito.
        </p>
        <button style={styles.ctaButton}>Solicitar Demo Gratuita</button>
      </section>
    </div>
  );
}
